// ===========================================
// Parametrages — Paramètres globaux (refonte UX)
// ===========================================
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Tabs, Form, Input, InputNumber, Button, Table, Grid,
    Space, Row, Col, Popconfirm, Modal, Tag, Empty, Alert, Divider,
} from 'antd';
import {
    SettingOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, EditOutlined,
    WarningOutlined, BankOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined,
    PercentageOutlined, DollarOutlined, AlertOutlined, MessageOutlined,
    DatabaseOutlined, KeyOutlined, IdcardOutlined,
} from '@ant-design/icons';
import { settingsService } from '../../common/services';

import './parametrages.page.scss';

// ─── DynamicListManager — gestion d'une liste {code, label} ─────────
const DynamicListManager = ({ settingKey, title, description, items, onUpdate, loading }) => {
    const [localItems, setLocalItems] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [modalForm] = Form.useForm();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLocalItems(Array.isArray(items) ? [...items] : []);
    }, [items]);

    const openAddModal = () => {
        setEditIndex(null);
        modalForm.resetFields();
        setModalVisible(true);
    };

    const openEditModal = (index) => {
        setEditIndex(index);
        modalForm.setFieldsValue(localItems[index]);
        setModalVisible(true);
    };

    const handleModalOk = async () => {
        try {
            const values = await modalForm.validateFields();
            const updated = [...localItems];
            if (editIndex !== null) {
                updated[editIndex] = {
                    code: values.code.trim().toLowerCase().replace(/\s+/g, '_'),
                    label: values.label.trim(),
                };
            } else {
                const code = values.code.trim().toLowerCase().replace(/\s+/g, '_');
                if (updated.some((item) => item.code === code)) {
                    toast.warning('Ce code existe déjà');
                    return;
                }
                updated.push({ code, label: values.label.trim() });
            }
            setSaving(true);
            await settingsService.update(settingKey, updated);
            onUpdate(settingKey, updated);
            setLocalItems(updated);
            setModalVisible(false);
            toast.success(editIndex !== null ? 'Élément modifié' : 'Élément ajouté');
        } catch (err) {
            if (err.errorFields) return;
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (index) => {
        const updated = localItems.filter((_, i) => i !== index);
        try {
            setSaving(true);
            await settingsService.update(settingKey, updated);
            onUpdate(settingKey, updated);
            setLocalItems(updated);
            toast.success('Élément supprimé');
        } catch (err) {
            toast.error('Erreur lors de la suppression');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: '#',
            width: 50,
            align: 'center',
            render: (_, __, i) => <span className="param-list-index">{i + 1}</span>,
        },
        {
            title: 'Code',
            dataIndex: 'code',
            width: 200,
            render: (code) => <Tag color="geekblue" className="param-list-code-tag">{code}</Tag>,
        },
        {
            title: 'Libellé',
            dataIndex: 'label',
            render: (label) => <span className="param-list-label">{label}</span>,
        },
        {
            title: 'Actions',
            width: 110,
            align: 'center',
            render: (_, record, index) => (
                <Space size={6}>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        className="param-list-action param-list-action-edit"
                        aria-label="Modifier"
                        onClick={() => openEditModal(index)}
                    />
                    <Popconfirm
                        title="Supprimer cet élément ?"
                        onConfirm={() => handleDelete(index)}
                        okText="Oui"
                        cancelText="Non"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            size="small"
                            icon={<DeleteOutlined />}
                            className="param-list-action param-list-action-delete"
                            aria-label="Supprimer"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="param-list">
            <div className="param-list-header">
                <div className="param-list-header-text">
                    <h4 className="param-list-title">{title}</h4>
                    {description && <p className="param-list-desc">{description}</p>}
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openAddModal}
                    className="param-list-btn-add"
                >
                    Ajouter
                </Button>
            </div>

            <Table
                dataSource={localItems}
                columns={columns}
                rowKey="code"
                size="small"
                pagination={false}
                loading={loading || saving}
                locale={{
                    emptyText: <Empty description="Aucun élément configuré" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                }}
                className="param-list-table"
            />

            <Modal
                title={
                    <span className="param-modal-title">
                        <SettingOutlined /> {editIndex !== null ? "Modifier l'élément" : 'Ajouter un élément'}
                    </span>
                }
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={() => setModalVisible(false)}
                confirmLoading={saving}
                okText={editIndex !== null ? 'Enregistrer' : 'Ajouter'}
                cancelText="Annuler"
                destroyOnClose
                className="param-modal"
                okButtonProps={{ className: 'param-modal-btn-ok' }}
            >
                <Form form={modalForm} layout="vertical" style={{ marginTop: 16 }} className="param-modal-form">
                    <Form.Item
                        name="code"
                        label="Code (identifiant unique)"
                        rules={[{ required: true, message: 'Le code est requis' }]}
                    >
                        <Input
                            prefix={<IdcardOutlined />}
                            placeholder="Ex : accident, vol, incendie…"
                            disabled={editIndex !== null}
                        />
                    </Form.Item>
                    <Form.Item
                        name="label"
                        label="Libellé (affiché dans les formulaires)"
                        rules={[{ required: true, message: 'Le libellé est requis' }]}
                    >
                        <Input placeholder="Ex : Accident de circulation" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

// ─── Page principale ────────────────────────────────────────────────
const Parametrages = () => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await settingsService.getAll();
                const raw = res?.data || res;
                const list = raw?.data || raw;
                if (Array.isArray(list)) {
                    const flat = {};
                    list.forEach((s) => { flat[s.key] = s.value; });
                    setSettings(flat);
                    form.setFieldsValue(flat);
                } else {
                    setSettings(list);
                    form.setFieldsValue(list);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async (values) => {
        try {
            setSaving(true);
            for (const [key, value] of Object.entries(values)) {
                if (value !== undefined && value !== null) {
                    await settingsService.update(key, value);
                }
            }
            toast.success('Paramètres enregistrés avec succès');
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleListUpdate = (key, newValue) => {
        setSettings((prev) => ({ ...prev, [key]: newValue }));
    };

    const handleSeed = async () => {
        try {
            setSeeding(true);
            await settingsService.seed();
            toast.success('Données initialisées avec succès');
            const res = await settingsService.getAll();
            const raw = res?.data || res;
            const list = raw?.data || raw;
            if (Array.isArray(list)) {
                const flat = {};
                list.forEach((s) => { flat[s.key] = s.value; });
                setSettings(flat);
                form.setFieldsValue(flat);
            }
        } catch (error) {
            toast.error("Erreur lors de l'initialisation");
        } finally {
            setSeeding(false);
        }
    };

    const hasNoSinistreData =
        (!settings.TYPES_SINISTRE || !Array.isArray(settings.TYPES_SINISTRE) || settings.TYPES_SINISTRE.length === 0) ||
        (!settings.STATUTS_SINISTRE || !Array.isArray(settings.STATUTS_SINISTRE) || settings.STATUTS_SINISTRE.length === 0) ||
        (!settings.CIRCONSTANCES_SINISTRE || !Array.isArray(settings.CIRCONSTANCES_SINISTRE) || settings.CIRCONSTANCES_SINISTRE.length === 0);

    // ─── Tabs ──────────────────────────────────────────────────────────
    const tabItems = [
        {
            key: 'general',
            label: (
                <span className="param-tab-label">
                    <BankOutlined />
                    <span className="tab-label-text">Général</span>
                </span>
            ),
            children: (
                <div className="param-section">
                    <div className="param-section-header">
                        <h3 className="param-section-title">
                            <BankOutlined /> Informations société
                        </h3>
                        <p className="param-section-subtitle">
                            Coordonnées affichées sur les documents générés (PDF, exports, bordereaux…)
                        </p>
                    </div>
                    <Form form={form} layout="vertical" onFinish={handleSave} className="param-form">
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="nomSociete" label="Nom de la société">
                                    <Input prefix={<BankOutlined />} placeholder="CAP Assurances" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="telephone" label="Téléphone">
                                    <Input prefix={<PhoneOutlined />} placeholder="0700000000" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="email" label="Email">
                                    <Input prefix={<MailOutlined />} placeholder="contact@capassurances.ci" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="adresse" label="Adresse">
                                    <Input prefix={<EnvironmentOutlined />} placeholder="Cocody 2 Plateaux Vallon…" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={saving}
                            icon={<SaveOutlined />}
                            className="param-btn-save"
                        >
                            Enregistrer
                        </Button>
                    </Form>
                </div>
            ),
        },
        {
            key: 'taux',
            label: (
                <span className="param-tab-label">
                    <PercentageOutlined />
                    <span className="tab-label-text">Taux & Taxes</span>
                </span>
            ),
            children: (
                <div className="param-section">
                    <div className="param-section-header">
                        <h3 className="param-section-title">
                            <PercentageOutlined /> Taux et taxes par défaut
                        </h3>
                        <p className="param-section-subtitle">
                            Valeurs globales appliquées aux contrats sans taux personnalisé
                        </p>
                    </div>
                    <Form form={form} layout="vertical" onFinish={handleSave} className="param-form">
                        <Row gutter={16}>
                            <Col xs={12} sm={8}>
                                <Form.Item name="tauxTaxe" label="Taux taxe (%)">
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        style={{ width: '100%' }}
                                        addonAfter="%"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Form.Item name="tauxFGA" label="Taux FGA (%)">
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        style={{ width: '100%' }}
                                        addonAfter="%"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Form.Item name="montantCEDEAO" label="Montant CEDEAO">
                                    <InputNumber
                                        min={0}
                                        style={{ width: '100%' }}
                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                        addonAfter="F"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Form.Item name="tauxCommissionDefault" label="Commission par défaut (%)">
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        style={{ width: '100%' }}
                                        addonAfter="%"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={saving}
                            icon={<SaveOutlined />}
                            className="param-btn-save"
                        >
                            Enregistrer
                        </Button>
                    </Form>
                </div>
            ),
        },
        {
            key: 'sinistres',
            label: (
                <span className="param-tab-label">
                    <AlertOutlined />
                    <span className="tab-label-text">Sinistres</span>
                </span>
            ),
            children: (
                <div className="param-section">
                    <div className="param-section-header">
                        <h3 className="param-section-title">
                            <AlertOutlined /> Référentiels sinistres
                        </h3>
                        <p className="param-section-subtitle">
                            Listes alimentant le formulaire de déclaration de sinistre
                        </p>
                    </div>

                    {hasNoSinistreData && (
                        <Alert
                            message="Aucune donnée configurée"
                            description="Les types de sinistre et circonstances ne sont pas encore initialisés. Cliquez sur le bouton ci-dessous pour charger les valeurs par défaut."
                            type="warning"
                            showIcon
                            icon={<WarningOutlined />}
                            className="param-alert-seed"
                            action={
                                <Button
                                    icon={<DatabaseOutlined />}
                                    loading={seeding}
                                    onClick={handleSeed}
                                    className="param-btn-seed"
                                >
                                    Initialiser
                                </Button>
                            }
                        />
                    )}

                    <DynamicListManager
                        settingKey="TYPES_SINISTRE"
                        title="Types de sinistre"
                        description="Liste des types de sinistre disponibles dans le formulaire de déclaration"
                        items={settings.TYPES_SINISTRE}
                        onUpdate={handleListUpdate}
                        loading={loading}
                    />
                    <Divider />
                    <DynamicListManager
                        settingKey="CIRCONSTANCES_SINISTRE"
                        title="Circonstances"
                        description="Liste des circonstances disponibles lors de la déclaration d'un sinistre"
                        items={settings.CIRCONSTANCES_SINISTRE}
                        onUpdate={handleListUpdate}
                        loading={loading}
                    />
                    <Divider />
                    <DynamicListManager
                        settingKey="STATUTS_SINISTRE"
                        title="Statuts de sinistre"
                        description="Liste des statuts disponibles pour le suivi des sinistres"
                        items={settings.STATUTS_SINISTRE}
                        onUpdate={handleListUpdate}
                        loading={loading}
                    />
                </div>
            ),
        },
        {
            key: 'sms',
            label: (
                <span className="param-tab-label">
                    <MessageOutlined />
                    <span className="tab-label-text">SMS</span>
                </span>
            ),
            children: (
                <div className="param-section">
                    <div className="param-section-header">
                        <h3 className="param-section-title">
                            <MessageOutlined /> Configuration SMS
                        </h3>
                        <p className="param-section-subtitle">
                            Identifiants et modèles pour l'envoi de SMS (relance, échéance)
                        </p>
                    </div>
                    <Form form={form} layout="vertical" onFinish={handleSave} className="param-form">
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="smsApiKey"
                                    label="Clé API SMS"
                                    extra="Cliquez sur l'œil pour révéler la clé."
                                >
                                    <Input.Password
                                        prefix={<KeyOutlined />}
                                        autoComplete="new-password"
                                        placeholder="Votre clé API"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="smsSenderId" label="Sender ID">
                                    <Input
                                        prefix={<IdcardOutlined />}
                                        placeholder="CAP-ASSUR"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            name="smsTemplateRelance"
                            label="Template SMS relance"
                            extra="Variables disponibles : {nom}, {numero_police}, {montant}"
                        >
                            <Input.TextArea rows={3} placeholder="Bonjour {nom}, votre prime…" />
                        </Form.Item>
                        <Form.Item
                            name="smsTemplateEcheance"
                            label="Template SMS échéance"
                            extra="Variables disponibles : {nom}, {numero_police}, {date_echeance}"
                        >
                            <Input.TextArea rows={3} placeholder="Bonjour {nom}, votre contrat…" />
                        </Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={saving}
                            icon={<SaveOutlined />}
                            className="param-btn-save"
                        >
                            Enregistrer
                        </Button>
                    </Form>
                </div>
            ),
        },
    ];

    return (
        <div className="parametrages-page">
            {/* Header */}
            <header className="param-header">
                <div className="param-header-text">
                    <h1 className="param-title">
                        <SettingOutlined /> Paramètres généraux
                    </h1>
                    <p className="param-subtitle">Configuration globale du back-office et des modules métier</p>
                </div>
            </header>

            {/* Tabs */}
            <Card className="param-main-card">
                <Tabs
                    items={tabItems}
                    defaultActiveKey="general"
                    className="param-tabs"
                    tabPosition={isMobile ? 'top' : 'top'}
                />
            </Card>
        </div>
    );
};

export default Parametrages;
